import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import type { SourceType } from '@trello/analytics-types';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { dontUpsell } from '@trello/browser';
import { Button } from '@trello/nachos/button';
import { SparkleIcon } from '@trello/nachos/icons/sparkle';
import { getOrganizationMembersUrl } from '@trello/urls';

import {
  useOpenPlanSelectionCallback,
  WithEnterpriseManagedOverride,
} from 'app/src/components/UpgradePrompts';

interface OverLimitButtonProps {
  workspaceId: string;
  isFreeTrialEligible: boolean;
  onCtaClick?: () => void;
  source?: SourceType;
}

export const OverLimitButton: FunctionComponent<OverLimitButtonProps> = ({
  workspaceId,
  isFreeTrialEligible,
  onCtaClick,
  source,
}) => {
  const openPlanSelection = useOpenPlanSelectionCallback(workspaceId, {
    redirect: false,
  });

  const handleButtonClick = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: isFreeTrialEligible
        ? 'startFreeTrialButton'
        : 'upgradeButton',
      source: source ?? 'workspaceChooserUserLimitExceededMessage',
      containers: formatContainers({ workspaceId }),
    });
    openPlanSelection();
    onCtaClick?.();
  }, [isFreeTrialEligible, source, workspaceId, openPlanSelection, onCtaClick]);

  const handleManageCollaboratorsLinkClick = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: 'workspaceMembersLink',
      source: 'workspaceChooserUserLimitExceededMessage',
      containers: formatContainers({ workspaceId }),
    });
    onCtaClick?.();
  }, [onCtaClick, workspaceId]);

  if (isFreeTrialEligible) {
    return (
      <Button
        appearance="primary"
        size="fullwidth"
        iconBefore={<SparkleIcon />}
        onClick={handleButtonClick}
      >
        <WithEnterpriseManagedOverride>
          <FormattedMessage
            id="templates.board_closed.start-free-trial"
            defaultMessage="Start free trial"
            description="Start free trial button text"
          />
        </WithEnterpriseManagedOverride>
      </Button>
    );
  }

  if (dontUpsell()) {
    return (
      <Button
        href={getOrganizationMembersUrl(workspaceId)}
        appearance="primary"
        size="fullwidth"
        isRouterLink
        onClick={handleManageCollaboratorsLinkClick}
      >
        <FormattedMessage
          id="templates.board_closed.manage-collaborators"
          defaultMessage="Manage collaborators"
          description="Manage collaborators link text"
        />
      </Button>
    );
  }

  return (
    <Button
      appearance="primary"
      size="fullwidth"
      iconBefore={<SparkleIcon />}
      onClick={handleButtonClick}
    >
      <WithEnterpriseManagedOverride>
        <FormattedMessage
          id="templates.board_closed.upgrade-workspace"
          defaultMessage="Upgrade Workspace"
          description="Upgrade Workspace button text"
        />
      </WithEnterpriseManagedOverride>
    </Button>
  );
};
